import { Button } from '../ui/button';
import React, { useMemo, useRef } from 'react';
import Modal from 'react-modal';
import { PriceDetail } from '../list/flightlist/types';
import { formatCurrency } from '@/lib/utils';
import { CircleX, Check } from 'lucide-react';
import { useRouter } from '@/lib/navigation';
import { useSelector, useDispatch } from 'react-redux';
import {
  setSelectedFlight,
  setSelectedReturnFlight,
} from '@flysync/shared-redux/slices/userInputSlice';
import { updateReviewData } from '@flysync/shared-redux/slices/flightSearchSlice';
import _ from 'lodash';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { useToast } from '@/hooks/use-toast';

const BucketFare = ({
  showModal,
  setShowModal,
}: {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
}) => {
  const dispatch = useDispatch();
  const userInput = useSelector(
    (state: { userInputs: any }) => state.userInputs,
  );
  const flightSearchInput = useSelector((state: any) => state.flightSearch);
  const [selectedBucket, setSelectedBucket] = React.useState<any>([]);
  const [selectedBucketIndex, setSelectedBucketIndex] = React.useState<any>([
    0,
    flightSearchInput?.returnBucket?.length > 0 &&
    flightSearchInput?.travelType === 'RT'
      ? 0
      : -1,
  ]);
  const [bucketList, setBucketList] = React.useState<PriceDetail[]>([]);
  const [activeIndex, setActiveIndex] = React.useState<number>(0);
  const router = useRouter();
  const [activeTab, setActiveTab] = React.useState(0);
  const { toast } = useToast();

  const searchButtonRef = useRef<HTMLButtonElement>(null);

  const onSelectBucketList = (index: number, val: PriceDetail) => {
    const newIndex = [...selectedBucketIndex];
    newIndex[activeIndex] = index;
    const newSelectedBucket = [...selectedBucket];
    newSelectedBucket[activeIndex] = val;
    setSelectedBucket(newSelectedBucket);
    setSelectedBucketIndex(newIndex);
  };

  React.useEffect(() => {
    setBucketList(flightSearchInput?.selectBucket);
  }, [flightSearchInput]);

  const handleTabChange = (index: number) => {
    setActiveIndex(index);
    if (index === 0) {
      setBucketList(flightSearchInput?.selectBucket);
    } else {
      setBucketList(flightSearchInput?.returnBucket);
    }
  };

  const onPressContinue = () => {
    // First flight validation
    if (!selectedBucket || !userInput?.selectedFlight?.price_details?.length) {
      toast({
        title: 'Error',
        description: 'Please select a fare option',
        variant: 'error',
      });
      return;
    }

    // Handle outbound flight
    const getSelectedFlight = userInput?.selectedFlight;
    const updatedFlight = {
      ...getSelectedFlight,
      price_details: [
        getSelectedFlight?.price_details[selectedBucketIndex[0] || 0],
      ],
    };
    dispatch(setSelectedFlight(updatedFlight));

    if (flightSearchInput?.tripType === 'INRT') {
      dispatch(setSelectedReturnFlight(null));
    }

    // Handle return flight
    if (
      flightSearchInput?.travelType === 'RT' &&
      flightSearchInput?.travelMode === 'DO'
    ) {
      // Check if return flight is selected (index should not be -1)
      if (selectedBucketIndex[1] === -1) {
        toast({
          title: 'Error',
          description: 'Please select a return fare option',
          variant: 'error',
        });
        return;
      }

      const returnFlight = userInput?.selectedReturnFlight;
      // If no specific return fare is selected, use the first one (index 0)
      const selectedReturnIndex =
        selectedBucketIndex[1] >= 0 ? selectedBucketIndex[1] : 0;

      const updatedReturnFlight = {
        ...returnFlight,
        price_details: [returnFlight?.price_details[selectedReturnIndex]],
      };
      dispatch(setSelectedReturnFlight(updatedReturnFlight));
    }

    const reqData = JSON.parse(sessionStorage.getItem('reqData') || '{}');

    localStorage.setItem('flightData', JSON.stringify(reqData));

    setShowModal(false);
    if (searchButtonRef.current) {
      searchButtonRef.current.click();
    }
    window.open('/flight-details', '_blank');
  };

  return (
    <Modal
      style={{
        content: {
          height: 'auto',
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-50%',
          transform: 'translate(-50%, -50%)',
          width: '95%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '16px',
          '@media (min-width: 768px)': {
            width: '80%',
            top: '10%',
            left: '42%',
            transform: 'translate(-50%, 0)',
            padding: '20px',
          },
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1000,
        },
      }}
      isOpen={showModal}
      onRequestClose={() => setShowModal(false)}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
    >
      {flightSearchInput?.travelType === 'RT' &&
        flightSearchInput?.travelMode === 'DO' && (
          <Tabs
            defaultValue={
              userInput?.selectedFlight?.origin +
              '-' +
              userInput?.selectedFlight?.destination
            }
            onValueChange={(value) => {
              const newIndex = [
                userInput?.selectedFlight,
                userInput?.selectedReturnFlight,
              ].findIndex(
                (flight) =>
                  flight?.origin + '-' + flight?.destination === value,
              );
              setActiveTab(newIndex !== -1 ? newIndex : 0);
              handleTabChange(newIndex);
            }}
          >
            <TabsList className="mx-auto flex flex-wrap justify-center gap-2 p-0 md:gap-3">
              {[userInput?.selectedFlight, userInput?.selectedReturnFlight].map(
                (flightData, index) => {
                  const tabValue =
                    flightData?.origin + '-' + flightData?.destination;
                  return (
                    <TabsTrigger
                      key={tabValue || index}
                      value={tabValue || ''}
                      className={`rounded-xl text-xs md:rounded-2xl md:text-sm ${
                        activeTab == index
                          ? 'text-white data-[state=active]:bg-primary'
                          : 'text-primary data-[state=active]:bg-light'
                      } bg-white px-3 py-1.5 text-primary data-[state=active]:text-white md:px-4 md:py-2`}
                    >
                      {tabValue || 'Flight'}
                    </TabsTrigger>
                  );
                },
              )}
            </TabsList>
          </Tabs>
        )}
      <div className="flex flex-col">
        <div className="mb-4 flex flex-col items-start justify-between md:mb-6 md:flex-row md:items-center">
          <div className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-2">
            <h1 className="text-lg font-bold text-primary md:text-2xl">{`${
              bucketList?.length
            } Fare Options`}</h1>
            <h1 className="text-lg font-bold md:text-2xl">
              available for your trip.
            </h1>
          </div>

          <CircleX
            className="absolute right-4 top-4 h-5 w-5 cursor-pointer md:relative md:right-0 md:top-0 md:h-6 md:w-6"
            onClick={() => setShowModal(false)}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3">
          {bucketList?.map((item: PriceDetail, index: number) => {
            return (
              <BucketFareComponent
                key={index}
                index={index}
                details={item}
                onClick={() => onSelectBucketList(index, item)}
                selectedBucket={selectedBucketIndex[activeIndex]}
              />
            );
          })}
        </div>
        <div className="mt-6 flex flex-1 justify-center md:mt-10">
          <Button
            variant="default"
            onClick={onPressContinue}
            className="w-full px-4 py-2 md:w-auto md:px-6 md:py-2.5"
          >
            <h4 className="text-sm text-tertiary-light md:text-base">
              Continue
            </h4>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

interface BucketFareComponentI {
  details: PriceDetail;
  selectedBucket: number;
  onClick: (index: number, val: PriceDetail) => void;
  index: number;
}
const BucketFareComponent = ({
  details,
  selectedBucket,
  onClick,
  index,
}: BucketFareComponentI) => {
  // Group rules by type
  const groupedRules = React.useMemo(() => {
    if (!details.miniFareRule?.length) return {};

    return details.miniFareRule.reduce(
      (acc: Record<string, any[]>, rule: Record<string, any>) => {
        const type = rule.Type || 'Other';
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(rule);
        return acc;
      },
      {},
    );
  }, [details.miniFareRule]);

  // Get the last rule for online reissue/refund info
  const lastRule = details.miniFareRule?.[details.miniFareRule.length - 1];

  const totalNetFare = useMemo(() => {
    return Number(details?.fareDetails?.net_fare) 
  }, [details])

  // - Number(details?.fareDetails?.admin_markup) - Number(details?.fareDetails?.admin_markup_gst)

  return (
    <div className="cursor-pointer" onClick={() => onClick(index, details)}>
      <div
        className={`relative flex h-full flex-col rounded-2xl border-2 bg-white p-5 transition-all duration-500 hover:scale-95 ${selectedBucket === index ? 'border-primary' : 'border-slate-200'}`}
      >
        <div>
          <div className="flex flex-row items-center justify-between">
            <div className="mb-2 inline-flex items-baseline">
              <span
                className="text-xl font-bold text-zinc-800 dark:text-slate-200"
                x-text="isAnnual ? '29' : '35'"
              >
                {formatCurrency(totalNetFare)}
              </span>
            </div>
            <div
              className={`mb-2 w-fit rounded px-4 py-1 text-xs font-semibold uppercase ${
                details?.fareType?.toLowerCase() === 'regular'
                  ? 'bg-green-100 text-green-800'
                  : details?.fareType?.toLowerCase() === 'sale'
                    ? 'bg-blue-100 text-blue-800'
                    : details?.fareType?.toLowerCase() == 'premium'
                      ? 'bg-yellow-100 text-yellow-800'
                      : details?.fareType?.toLowerCase() === 'flexi'
                        ? 'bg-orange-100 text-orange-800'
                        : details?.fareType?.toLowerCase() === 'private'
                          ? 'bg-violet-100 text-violet-800'
                          : details?.fareType?.toLowerCase() === 'sme'
                            ? 'bg-rose-100 text-rose-800'
                            : details?.fareType?.toLowerCase() === 'lite'
                              ? 'bg-stone-100 text-stone-800'
                              : details?.fareType?.toLowerCase() === 'flexi'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-blue-100 text-blue-800'
              }`}
            >
              {_.startCase(details?.fareType)}
            </div>
          </div>
          <div className="mb-2 text-xs text-slate-500">
            There are many variations available, but the majority have suffered.
          </div>
        </div>

        <div className="text-xs font-medium text-zinc-800 dark:text-slate-200">
          Includes
        </div>
        <ul className="grow space-y-3 text-xs text-text_secondary dark:text-slate-400">
          <li className="flex items-center">
            <Check className="mr-3 h-3 w-3 shrink-0 text-emerald-500" />
            <span>{details.checkin_baggage} Check-in Baggage</span>
          </li>
          <li className="flex items-center">
            <Check className="mr-3 h-3 w-3 shrink-0 text-emerald-500" />
            <span>{details.hand_baggage} Hand Baggage</span>
          </li>

          {Object.entries(groupedRules).map(([type, rules]) => (
            <li key={`rule-group-${type}`}>
              <div className="mb-1 text-xs font-medium text-zinc-800">
                {type} Fee
              </div>
              {rules.map((rule, ruleIndex) => (
                <div
                  key={`rule-${type}-${ruleIndex}`}
                  className="flex items-start"
                >
                  <Check className="mr-3 mt-1 h-3 w-3 shrink-0 text-emerald-500" />
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500">
                      {rule?.Details.toLowerCase() !== 'nil'
                        ? rule?.Details
                        : ''}
                    </span>
                    {(rule?.From || rule?.To) && (
                      <span className="text-xs text-gray-500">
                        {rule?.From && `From: ${rule?.From} ${rule?.Unit}`}
                        {rule?.To && ` To: ${rule?.To} ${rule?.Unit}`}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </li>
          ))}

          {lastRule && (
            <li>
              <div className="mt-1 space-y-1">
                <span className="flex items-center text-xs">
                  {lastRule?.OnlineReissueAllowed ? (
                    <>
                      <Check className="mr-1 h-3 w-3 text-emerald-500" />
                      <span>Online Reissue Available</span>
                    </>
                  ) : (
                    <>
                      <CircleX className="mr-1 h-3 w-3 text-red-500" />
                      <span>Online Reissue Not Available</span>
                    </>
                  )}
                </span>
              </div>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default BucketFare;
